import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../contexts/LanguageContext";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { LogOut, Globe } from "lucide-react";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [language, setLanguage] = useState(user?.language || lang);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", { name, language });
      setUser(r.data);
      setLang(language);
      toast.success(t.dash.saved);
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/", { replace: true }); };

  if (!user) return null;

  return (
    <div className="container-pad mx-auto max-w-[720px] py-12" data-testid="page-profile">
      <h1 className="text-3xl font-extrabold text-navy">{t.dash.profile}</h1>

      <div className="mt-8 bg-white border border-slate-100 rounded-[24px] p-7 md:p-9 shadow-soft">
        <div className="flex items-center gap-4">
          <span className="w-16 h-16 rounded-2xl bg-navy text-white inline-flex items-center justify-center text-2xl font-extrabold">{(user.name || user.email)[0].toUpperCase()}</span>
          <div>
            <div className="font-bold text-navy text-lg" data-testid="profile-name">{user.name || "—"}</div>
            <div className="text-slate-600 text-sm" data-testid="profile-email">{user.email}</div>
          </div>
        </div>

        <form onSubmit={save} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="p-name">{t.apply.fullName}</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-xl" data-testid="profile-name-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Globe size={14} /> {t.dash.language}</Label>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => setLanguage("en")} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${language === "en" ? "bg-navy text-white" : "bg-soft text-slate-600"}`} data-testid="profile-lang-en">{t.dash.english}</button>
              <button type="button" onClick={() => setLanguage("es")} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${language === "es" ? "bg-navy text-white" : "bg-soft text-slate-600"}`} data-testid="profile-lang-es">{t.dash.spanish}</button>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="flex-1 px-6 py-3.5 rounded-full bg-brandred hover:bg-red-700 text-white font-semibold disabled:opacity-60" data-testid="profile-save">
              {saving ? t.common.loading : t.dash.save}
            </button>
            <button type="button" onClick={handleLogout} className="px-5 py-3.5 rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold transition-colors inline-flex items-center gap-2" data-testid="profile-logout">
              <LogOut size={16} /> {t.dash.logout}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
